import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import AdminNav from "../components/adminNav";
import Time from "../components/time";
import { useParams, Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";

/* =============== helpers =============== */
const pad2 = (n) => String(n).padStart(2, "0");
const formatDMY = (dLike) => {
  const d = new Date(dLike);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const format12h = (d) => {
  const h = d.getHours();
  const m = d.getMinutes();
  const period = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${period}`;
};
const decToHHMM = (decimalHrs) => {
  if (decimalHrs === null || decimalHrs === undefined || decimalHrs === "") return "";
  const n = Number(decimalHrs);
  if (Number.isNaN(n)) return String(decimalHrs);
  const hh = Math.floor(n);
  const mm = Math.round((n - hh) * 60);
  return `${pad2(hh)}:${pad2(mm)}`;
};
const decToAMPM = (decimalHrs) => {
  const hhmm = decToHHMM(decimalHrs);
  if (!hhmm.includes(":")) return "";
  const [hh, mm] = hhmm.split(":").map((x) => parseInt(x, 10));
  const t = new Date();
  t.setHours(hh, mm, 0, 0);
  return format12h(t);
};
const formatWorkingTotal = (hoursDecimal) => {
  if (hoursDecimal === null || hoursDecimal === undefined || hoursDecimal === "") return "";
  const n = Number(hoursDecimal);
  if (Number.isNaN(n)) return String(hoursDecimal);
  if (n < 24) {
    const s = Number.isInteger(n) ? `${n}` : `${parseFloat(n.toFixed(1))}`;
    return `${s} hours`;
  }
  const days = Math.floor(n / 24);
  const rem = n - days * 24;
  if (Math.abs(rem) < 1e-6) return `${days} day${days > 1 ? "s" : ""}`;
  const remStr = Number.isInteger(rem) ? `${rem}` : `${parseFloat(rem.toFixed(1))}`;
  return `${days} day${days > 1 ? "s" : ""} ${remStr}h`;
};
const chipStyle = (highlight) => ({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.6,
  background: highlight ? "#fff7ed" : "#eef2ff",
  color: highlight ? "#9a3412" : "#3730a3",
});
const cellStack = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 6,
};
/* ======================================= */

const HireVehReport = () => {
  useAuthRedirect();
  const { id, branch, basis } = useParams();
  const [data, setData] = useState([]);
  const [vehicleBasis, setVehicleBasis] = useState("");
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const startYear = 2007;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const canView = hasPermission("Vehicle Position Report", "view");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://rfs2.infraconsoft.net.in/api/get_hire_veh_report_api", {
        params: { month: selectedMonth, year: selectedYear, branch, id },
      });
      const responseData = response.data.postionData || [];
      setData(responseData);
      setFilteredData(responseData);
      if (responseData.length > 0 && responseData[0].fld_vbasis) {
        setVehicleBasis(responseData[0].fld_vbasis);
      }
    } catch (error) {
      console.error("Error fetching postionData:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = (e.target.value || "").toLowerCase();
    setSearchTerm(term);
    if (!term) {
      setFilteredData(data);
      return;
    }
    const filtered = data.filter(
      (item) =>
        String(item.stn_purnumber || "").toLowerCase().includes(term) ||
        String(item.stn_dpt || "").toLowerCase().includes(term)
    );
    setFilteredData(filtered);
  };

  // pagination
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  /* ========== Excel export (mirrors the table formatting) ========== */
  const exportToExcel = () => {
    let pageCumKm = 0;
    let pageCumHrs = 0;

    const exportData = currentData.map((item, i) => {
      const dateStr = formatDMY(item.fld_vdate);
      if (basis === "Hour") {
        const startAMPM = decToAMPM(item.fld_sthrs);
        const endAMPM = decToAMPM(item.fld_endhrs);
        const rowHrs = Number(item.fld_wohrs || 0);
        pageCumHrs += rowHrs;

        return {
          "S.No": indexOfFirstItem + i + 1,
          "Date": dateStr,
          "Starting Time": startAMPM,
          "Closing Time": endAMPM,
          "Working Hours": formatWorkingTotal(rowHrs),
          "Cumulative Hours": pageCumHrs.toFixed(2),
          "Number Of Loads": item.fld_noload,
          "Remarks": item.fld_remarks,
        };
      } else {
        // Km family
        const stkm = parseFloat(item.fld_stkm || 0);
        const endkm = parseFloat(item.fld_endkm || 0);
        const wkm = Math.round(endkm - stkm);
        pageCumKm += wkm;
        const breakHH = pad2(item?.fld_brehrs ?? 0);
        const breakMM = pad2(item?.fld_bremin ?? 0);

        return {
          "S.No": indexOfFirstItem + i + 1,
          "Date": dateStr,
          "Breaking Hours": `${breakHH}:${breakMM}`,
          "Starting KM": stkm,
          "Closing KM": endkm,
          "Working KM": wkm,
          "Cumulative KM": pageCumKm,
          "Number Of Loads": item.fld_noload,
          "Remarks": item.fld_remarks,
        };
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vehicle Details");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(blob, `vehicle_details_${selectedMonth}-${selectedYear}.xlsx`);
  };
  /* ================================================================ */

  if (!canView) {
    return (
      <div id="admin-container">
        <AdminNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <AdminNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Hire Vehicle Position</h2>
            <Link to={`/vehicle_position_report_hire`}>
              <p className="back-button"><i className="fa-solid fa-backward"></i> Back</p>
            </Link>
          </div>
          <Time />
        </section>

        <div className="add-new-container">
          <button className="add-new-btn" onClick={exportToExcel}>
            <i className="fa-solid fa-file-excel"></i> Export to Excel
          </button>
        </div>

        <div className="month-year-container">
          <p className="title">Select your Month & Year:</p>
          <div className="select-container">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="custom-select"
            >
              {[...Array(12).keys()].map((i) => (
                <option key={i + 1} value={i + 1}>{pad2(i + 1)}</option>
              ))}
            </select>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="custom-select"
            >
              {[...Array(currentYear - startYear + 1).keys()].map((i) => (
                <option key={startYear + i} value={startYear + i}>{startYear + i}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Date</th>

                {basis === "Hour" ? (
                  <>
                    <th>Starting Hours</th>
                    <th>Closing Hours</th>
                    <th>Working Hours</th>
                    <th>Cumulative Hours</th>
                  </>
                ) : (
                  <>
                    <th>Breaking Hours</th>
                    <th>Starting KM</th>
                    <th>Closing KM</th>
                    <th>Working KM</th>
                    <th>Cumulative KM</th>
                  </>
                )}

                <th>Number Of Loads</th>
                <th>Remarks</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="16" className="loading-text">Loading data, please wait...</td>
                </tr>
              ) : currentData.length > 0 ? (
                (() => {
                  let pageCumKm = 0;
                  let pageCumHrs = 0;

                  return currentData.map((item, index) => {
                    const dateStr = formatDMY(item.fld_vdate);

                    if (basis === "Hour") {
                      const rowHrs = Number(item.fld_wohrs || 0);
                      pageCumHrs += rowHrs;

                      const startAMPM = decToAMPM(item.fld_sthrs);
                      const endAMPM = decToAMPM(item.fld_endhrs);
                      const workingPretty = formatWorkingTotal(rowHrs);
                      const isMultiDay = rowHrs >= 24;

                      return (
                        <tr key={index}>
                          <td>{indexOfFirstItem + index + 1}</td>
                          <td>{new Date(item.fld_vdate).toLocaleDateString("en-GB")}</td>

                          {/* Start */}
                          <td style={{ verticalAlign: "top" }}>
                            <div style={cellStack}>
                              <span style={chipStyle(isMultiDay)}>{dateStr}</span>
                              <span style={{ fontWeight: 700 }}>{startAMPM || "—"}</span>
                            </div>
                          </td>

                          {/* End */}
                          <td style={{ verticalAlign: "top" }}>
                            <div style={cellStack}>
                              <span style={chipStyle(isMultiDay)}>{dateStr}</span>
                              <span style={{ fontWeight: 700 }}>{endAMPM || "—"}</span>
                            </div>
                          </td>

                          <td>{workingPretty || "—"}</td>
                          <td>{pageCumHrs.toFixed(2)}</td>

                          <td>{item.fld_noload}</td>
                          <td>{item.fld_remarks}</td>
                        </tr>
                      );
                    } else {
                      // Km family
                      const stkm = parseFloat(item.fld_stkm || 0);
                      const endkm = parseFloat(item.fld_endkm || 0);
                      const wkm = Math.round(endkm - stkm);
                      pageCumKm += wkm;

                      return (
                        <tr key={index}>
                          <td>{indexOfFirstItem + index + 1}</td>
                          <td>{new Date(item.fld_vdate).toLocaleDateString("en-GB")}</td>

                          <td>{pad2(item?.fld_brehrs ?? 0)}:{pad2(item?.fld_bremin ?? 0)}</td>
                          <td>{stkm}</td>
                          <td>{endkm}</td>
                          <td>{wkm}</td>
                          <td>{pageCumKm}</td>

                          <td>{item.fld_noload}</td>
                          <td>{item.fld_remarks}</td>
                        </tr>
                      );
                    }
                  });
                })()
              ) : (
                <tr>
                  <td colSpan="16" align="center">No Data Available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
            Previous
          </button>

          <button onClick={() => paginate(1)} className={currentPage === 1 ? "active" : ""}>
            1
          </button>

          {filteredData.length > 0 &&
            [...Array(Math.min(3, Math.ceil(filteredData.length / itemsPerPage) - 1))].map((_, index) => {
              const pageNumber = currentPage + index;
              if (pageNumber > 1 && pageNumber <= Math.ceil(filteredData.length / itemsPerPage)) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={currentPage === pageNumber ? "active" : ""}
                  >
                    {pageNumber}
                  </button>
                );
              }
              return null;
            })}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default HireVehReport;
